import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  model,
  effect,
  inject,
  signal,
  OnInit,
} from '@angular/core';
import { DateRangePickerComponent as LibDateRangePicker } from 'nebula-ui-kit';
import { catchError, of, tap } from 'rxjs';
import {
  AlertStore,
  TranslationService,
  DateRangePickerServices,
  Module,
} from '@nebula-shared/data-access';

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [LibDateRangePicker],
  templateUrl: './date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent extends AlertStore implements OnInit {
  protected service = inject(DateRangePickerServices);
  protected translation = inject(TranslationService);

  protected t(key: string): string {
    return this.translation.translate(key);
  }

  start = input<Date | null>(null);
  end = input<Date | null>(null);
  minDate = input<Date | null>(null);
  maxDate = input<Date | null>(null);
  module = input<Module>('accounting');

  startLabel = input<string>(this.t('ACCOUNTING.ANNULLMENT.PLACEHOLDER.DATE_START'));
  endLabel = input<string>(this.t('ACCOUNTING.ANNULLMENT.PLACEHOLDER.DATE_END'));
  placeholder = input<string>('dd/MM/yyyy');

  startChange = output<Date | null>();
  endChange = output<Date | null>();
  rangeChange = output<{ start: Date | null; end: Date | null }>();

  startModel = model<Date | null>(null);
  endModel = model<Date | null>(null);
  maxDateModel = signal<Date | undefined>(undefined);

  constructor() {
    super();
    effect(() => {
      const systemDateNow = this.systemDate();
      if (systemDateNow !== null) {
        this.getDefaultStart();
        this.getDefaultEnd();
      }
    });
  }

  ngOnInit(): void {
    this.getSystemParams();
  }

  systemDate = signal<Date | null>(null);

  onStartChange(date: Date | null): void {
    this.startChange.emit(date);
    this.rangeChange.emit({ start: date, end: this.endModel() });
  }

  onEndChange(date: Date | null): void {
    this.endChange.emit(date);
    this.rangeChange.emit({ start: this.startModel(), end: date });
  }

  onRangeChange(range: { start: Date | null; end: Date | null }): void {
    this.rangeChange.emit(range);
  }

  private getDefaultStart() {
    const systemDateNow = this.systemDate();
    const now = systemDateNow ?? new Date();
    const value = new Date(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01` + 'T00:00:00',
    );
    this.startModel.set(value);
  }

  private getDefaultEnd() {
    const systemDateNow = this.systemDate();
    const now = systemDateNow ?? new Date();
    const value = new Date(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}` +
        'T00:00:00',
    );
    this.endModel.set(value);
    this.maxDateModel.set(value);
  }

  getSystemParams() {
    this.service
      .getSystemParams(this.module())
      .pipe(
        tap((response) => {
          this.systemDate.set(new Date(response.fechaSistema));
        }),
        catchError((error) => {
          this.setError(error?.error.message || 'No se pudo cargar fecha del sistema');
          this.systemDate.set(null);
          return of(null);
        }),
      )
      .subscribe();
  }
}
